// =============================================================================
// Refunds / Void — faithful port of POS frmRefund + frmRefundMethod + lookup.
// Workflow: look up original transaction by invoice number -> select refund
// quantity per line -> choose refund method -> save refund + refund_items.
// =============================================================================
import { useEffect, useState, useCallback } from 'react'
import { Search, RotateCcw, X } from 'lucide-react'
import { getInvoiceByNumber, createRefund, listRefunds, listCustomers, adjustCustomerStoreCredit } from '../services/data'
import { useAuth } from '../context/AuthContext'
import { PageHeader, Card, DataTable, Pagination, Spinner, Button } from '../components/shared/ui'
import { currency, date, customerLabel } from '../lib/format'
import type { Invoice, InvoiceItem, Refund, Customer } from '../types'
import { toast } from 'react-toastify'

const PAGE_SIZE = 25
const REFUND_METHODS = ['Cash', 'Credit Card', 'Debit Card', 'Check', 'Store Credit', 'Gift Card']

interface RefundLine {
  item: InvoiceItem
  origQty: number
  refundQty: number
}

export default function Refunds() {
  const { user } = useAuth()
  const [lookupNum, setLookupNum] = useState('')
  const [invoice, setInvoice] = useState<Invoice | null>(null)
  const [lines, setLines] = useState<RefundLine[]>([])
  const [method, setMethod] = useState('Cash')
  const [customer, setCustomer] = useState<Customer | null>(null)
  const [custTerm, setCustTerm] = useState('')
  const [custResults, setCustResults] = useState<Customer[]>([])
  const [processing, setProcessing] = useState(false)
  const [refunds, setRefunds] = useState<Refund[]>([])
  const [count, setCount] = useState(0)
  const [page, setPage] = useState(0)
  const [loadingList, setLoadingList] = useState(true)

  const loadList = useCallback(async () => {
    setLoadingList(true)
    try { const { rows, count } = await listRefunds({ page, pageSize: PAGE_SIZE }); setRefunds(rows); setCount(count) }
    finally { setLoadingList(false) }
  }, [page])

  useEffect(() => { loadList() }, [loadList])

  // Customer search (needed to credit store-credit refunds, like ProcessStoreCreditRefund).
  useEffect(() => {
    if (!custTerm.trim()) { setCustResults([]); return }
    const t = setTimeout(async () => {
      try { const { rows } = await listCustomers({ search: custTerm, page: 0, pageSize: 8 }); setCustResults(rows) }
      catch { /* ignore */ }
    }, 250)
    return () => clearTimeout(t)
  }, [custTerm])

  const doLookup = async () => {
    const n = Number(lookupNum.trim())
    if (!n) { toast.error('Enter an invoice number'); return }
    try {
      const res = await getInvoiceByNumber(n)
      if (!res) { toast.info('No transaction found with that invoice number'); setInvoice(null); setLines([]); return }
      setInvoice(res.invoice)
      setLines(res.items.map(it => ({ item: it, origQty: it.qty ?? 0, refundQty: it.qty ?? 0 })))
    } catch { toast.error('Lookup failed') }
  }

  const setRefundQty = (id: string, qty: number) =>
    setLines(prev => prev.map(l => l.item.id === id ? { ...l, refundQty: Math.max(0, Math.min(l.origQty, qty)) } : l))

  // Refund totals (positive numbers representing money returned).
  const refundLines = lines.filter(l => l.refundQty > 0)
  const refundSubtotal = refundLines.reduce((s, l) => s + (l.item.price ?? 0) * l.refundQty, 0)
  const refundTax = refundLines.reduce((s, l) => {
    const pct = l.item.tax_percent ?? 0
    return s + (l.item.price ?? 0) * l.refundQty * (pct / 100)
  }, 0)
  const refundTotal = refundSubtotal + refundTax
  const refundQtyTotal = refundLines.reduce((s, l) => s + l.refundQty, 0)

  const processRefund = async () => {
    if (!invoice || refundLines.length === 0) { toast.error('Select at least one item to refund'); return }
    if (method === 'Store Credit' && !customer) {
      toast.error('Select a customer to credit the store-credit refund to')
      return
    }
    setProcessing(true)
    try {
      await createRefund(
        {
          refund_date: new Date().toISOString(),
          invoice_id: invoice.id,
          legacy_invoice_number: invoice.legacy_invoice_number,
          clerk_name: user?.email ?? null,
          taxable_amount: refundSubtotal,
          tax: refundTax,
          total: refundTotal,
          total_qty: refundQtyTotal,
          total_price: refundSubtotal,
          payment_method: method,
          is_active: true,
        },
        refundLines.map(l => ({
          product_id: l.item.product_id,
          product_name: l.item.product_name,
          price: l.item.price,
          qty: l.refundQty,
          tax: (l.item.price ?? 0) * l.refundQty * ((l.item.tax_percent ?? 0) / 100),
          total_price: (l.item.price ?? 0) * l.refundQty,
          tax_percent: l.item.tax_percent,
        })),
      )
      // Faithful side-effect: a Store Credit refund credits the customer's balance.
      if (method === 'Store Credit' && customer) {
        try { await adjustCustomerStoreCredit(customer.id, refundTotal) }
        catch (e) { console.error('store credit refund credit failed', e) }
      }
      toast.success(`Refund processed — ${currency(refundTotal)} via ${method}`)
      setInvoice(null); setLines([]); setLookupNum(''); setCustomer(null); setCustTerm(''); loadList()
    } catch (e) { console.error(e); toast.error('Failed to process refund') } finally { setProcessing(false) }
  }

  return (
    <div>
      <PageHeader title="Refunds / Void" subtitle="Look up an original transaction to refund" />

      <Card className="p-4 mb-4">
        <div className="flex gap-2 items-center">
          <input value={lookupNum} onChange={e => setLookupNum(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') doLookup() }}
            placeholder="Original invoice number…"
            className="flex-1 max-w-xs px-4 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-green-500 text-sm" />
          <Button onClick={doLookup}><Search size={16} /> Look Up</Button>
        </div>
      </Card>

      {invoice && (
        <Card className="p-5 mb-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-semibold text-gray-800">Invoice #{invoice.legacy_invoice_number} — {date(invoice.invoice_date)}</h3>
            <span className="text-sm text-gray-500">Original total {currency(invoice.total)}</span>
          </div>

          <table className="w-full text-sm mb-4">
            <thead>
              <tr className="text-gray-600 border-b border-gray-200">
                <th className="text-left font-semibold px-2 py-2">Item</th>
                <th className="text-right font-semibold px-2 py-2">Price</th>
                <th className="text-right font-semibold px-2 py-2">Orig Qty</th>
                <th className="text-right font-semibold px-2 py-2 w-28">Refund Qty</th>
                <th className="text-right font-semibold px-2 py-2">Refund</th>
              </tr>
            </thead>
            <tbody>
              {lines.map(l => (
                <tr key={l.item.id} className="border-b border-gray-100">
                  <td className="px-2 py-1.5">{l.item.product_name}</td>
                  <td className="px-2 py-1.5 text-right">{currency(l.item.price)}</td>
                  <td className="px-2 py-1.5 text-right">{l.origQty}</td>
                  <td className="px-2 py-1.5 text-right">
                    <input type="number" value={l.refundQty} min={0} max={l.origQty}
                      onChange={e => setRefundQty(l.item.id, parseFloat(e.target.value) || 0)}
                      className="w-20 text-right px-2 py-1 rounded border border-gray-200" />
                  </td>
                  <td className="px-2 py-1.5 text-right font-medium">{currency((l.item.price ?? 0) * l.refundQty)}</td>
                </tr>
              ))}
            </tbody>
          </table>

          <div className="flex flex-col md:flex-row gap-4 justify-between items-start md:items-end">
            <div>
              <label className="text-sm font-medium text-gray-700">Refund Method</label>
              <div className="grid grid-cols-3 gap-2 mt-2 max-w-md">
                {REFUND_METHODS.map(m => (
                  <button key={m} onClick={() => setMethod(m)}
                    className={`px-2 py-2 rounded-lg text-xs font-medium border ${method === m ? 'bg-green-700 text-white border-green-700' : 'border-gray-300 text-gray-700 hover:bg-gray-50'}`}>
                    {m}
                  </button>
                ))}
              </div>

              {method === 'Store Credit' && (
                <div className="mt-3 max-w-md">
                  <label className="text-sm font-medium text-gray-700">Credit to customer</label>
                  {customer ? (
                    <div className="mt-1 flex items-center justify-between rounded-lg border border-gray-200 px-3 py-2">
                      <div>
                        <div className="font-medium text-gray-800 text-sm">{customerLabel(customer)}</div>
                        <div className="text-xs text-gray-500">Current store credit {currency(customer.store_credit ?? 0)}</div>
                      </div>
                      <button onClick={() => setCustomer(null)} className="text-gray-400 hover:text-red-600"><X size={15} /></button>
                    </div>
                  ) : (
                    <div className="relative mt-1">
                      <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                      <input value={custTerm} onChange={e => setCustTerm(e.target.value)} placeholder="Search customer by name, phone…"
                        className="w-full pl-9 pr-3 py-2 rounded-lg border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-green-500" />
                      {custResults.length > 0 && (
                        <div className="absolute z-10 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-56 overflow-auto">
                          {custResults.map(c => (
                            <button key={c.id} onClick={() => { setCustomer(c); setCustTerm(''); setCustResults([]) }}
                              className="block w-full text-left px-3 py-2 hover:bg-green-50 text-sm border-b border-gray-50 last:border-0">
                              <div className="font-medium text-gray-800">{customerLabel(c)}</div>
                              <div className="text-xs text-gray-500">{[c.city, c.state].filter(Boolean).join(', ')} · credit {currency(c.store_credit ?? 0)}</div>
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
            <div className="text-right">
              <div className="text-sm text-gray-600">Subtotal {currency(refundSubtotal)}</div>
              <div className="text-sm text-gray-600">Tax {currency(refundTax)}</div>
              <div className="text-xl font-bold text-gray-900">Refund Total {currency(refundTotal)}</div>
              <Button variant="danger" className="mt-2" disabled={processing || refundLines.length === 0} onClick={processRefund}>
                <RotateCcw size={16} /> {processing ? 'Processing…' : 'Process Refund'}
              </Button>
            </div>
          </div>
        </Card>
      )}

      <Card className="p-4">
        <h3 className="font-semibold text-gray-800 mb-3">Recent Refunds</h3>
        {loadingList ? <Spinner label="Loading refunds…" /> : (
          <>
            <DataTable
              rows={refunds}
              emptyMessage="No refunds recorded"
              columns={[
                { key: 'refund_date', header: 'Date', render: r => date(r.refund_date) },
                { key: 'legacy_invoice_number', header: 'Invoice #', render: r => r.legacy_invoice_number ?? '—' },
                { key: 'clerk_name', header: 'Clerk', render: r => r.clerk_name || '—' },
                { key: 'payment_method', header: 'Method', render: r => r.payment_method || '—' },
                { key: 'total_qty', header: 'Qty', render: r => r.total_qty ?? 0, className: 'text-right' },
                { key: 'total', header: 'Total', render: r => currency(r.total), className: 'text-right' },
              ]}
            />
            <Pagination page={page} pageSize={PAGE_SIZE} total={count} onPage={setPage} />
          </>
        )}
      </Card>
    </div>
  )
}
